import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminApi, User } from '../services/api';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationToken, setPaginationToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // Status helper functions
  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return { label: 'Confirmed', color: 'success' as const };
      case 'FORCE_CHANGE_PASSWORD':
        return { label: 'Invited - Pending Login', color: 'warning' as const };
      case 'UNCONFIRMED':
        return { label: 'Pending Verification', color: 'warning' as const };
      case 'ARCHIVED':
        return { label: 'Archived', color: 'default' as const };
      case 'COMPROMISED':
        return { label: 'Compromised', color: 'error' as const };
      case 'UNKNOWN':
        return { label: 'Unknown', color: 'default' as const };
      case 'RESET_REQUIRED':
        return { label: 'Reset Required', color: 'warning' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', firstName: '', lastName: '', organizationId: 'default-org' });
  const [creating, setCreating] = useState(false);
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);

  const fetchUsers = useCallback(async (token?: string) => {
    try {
      setLoading(true);
      const response = await adminApi.listUsers({
        limit: 50,
        paginationToken: token,
        includeUsage: false, // Usage data available in user detail view - avoids N+1 queries
      });
      
      if (response.success) {
        if (token) {
          setUsers(prev => [...prev, ...response.data.users]);
        } else {
          setUsers(response.data.users);
        }
        setPaginationToken(response.data.pagination.nextToken);
        setHasMore(response.data.pagination.hasMore);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [fetchUsers]);

  const fetchOrganizations = async () => {
    try {
      const response = await adminApi.listOrganizations();
      if (response.success) {
        setOrganizations(response.data.organizations.map((org: any) => ({
          // CRITICAL FIX: API returns 'id', not 'organizationId'
          // Using org.name as fallback was storing NAME instead of ID!
          id: org.id || org.organizationId || org.name,
          name: org.name || org.displayName || org.id
        })));
      }
    } catch (err) {
      console.warn('Failed to fetch organizations:', err);
    }
  };

  const handleRefresh = () => {
    setPaginationToken(undefined);
    fetchUsers();
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/users/${params.row.userId}`);
  };

  const handleRowContextMenu = (e: React.MouseEvent, userId: string) => {
    e.preventDefault(); // Prevent default context menu
    // Open in new tab
    window.open(`/users/${userId}`, '_blank');
  };

  const handleCreateUser = async () => {
    if (!newUser.email) return;

    try {
      setCreating(true);
      await adminApi.createUser({
        email: newUser.email.toLowerCase(),
        firstName: newUser.firstName || undefined,
        lastName: newUser.lastName || undefined,
        organizationId: newUser.organizationId,
        isPaid: true,
        sendInvitation: true,
      });
      
      setCreateDialogOpen(false);
      setNewUser({ email: '', firstName: '', lastName: '', organizationId: 'default-org' });
      handleRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleUserStatus = async (e: React.MouseEvent, userId: string, isEnabled: boolean) => {
    e.stopPropagation(); // Prevent row click navigation
    try {
      if (isEnabled) {
        await adminApi.disableUser(userId);
      } else {
        await adminApi.enableUser(userId);
      }
      handleRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organizationId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (value, row) => {
        const firstName = row.firstName || '';
        const lastName = row.lastName || '';
        return `${firstName} ${lastName}`.trim() || '-';
      },
    },
    {
      field: 'organizationId',
      headerName: 'Organization',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value || 'default'} 
          size="small" 
          variant="outlined"
        />
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Account',
      width: 90,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Enabled' : 'Disabled'}
          size="small"
          variant="outlined"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Confirmation',
      flex: 0.6,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          size="small"
          {...getStatusChipProps(params.value)}
        />
      ),
    },
    {
      field: 'isPaid',
      headerName: 'Paid',
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          color={params.value ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 110,
      valueGetter: (value: string | null) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.row.isEnabled ? 'Disable User' : 'Enable User'}>
          <IconButton 
            size="small" 
            onClick={(e) => handleToggleUserStatus(e, params.row.userId, params.row.isEnabled)}
            color={params.row.isEnabled ? 'error' : 'success'}
          >
            {params.row.isEnabled ? <DisableIcon fontSize="small" /> : <EnableIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Users
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row.userId}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={handleRowClick}
          autoHeight
          slotProps={{
            row: {
              onContextMenu: (e: React.MouseEvent<HTMLDivElement>) => {
                const target = e.currentTarget;
                const userId = target.getAttribute('data-id');
                if (userId) {
                  handleRowContextMenu(e, userId);
                }
              },
            },
          }}
          sx={{
            border: 'none',
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          }}
        />

        {hasMore && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button onClick={() => fetchUsers(paginationToken)} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Load More'}
            </Button>
          </Box>
        )}
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
              placeholder="Email *"
            />
            <TextField
              label="First Name"
              value={newUser.firstName}
              onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={newUser.lastName}
              onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Organization ID</InputLabel>
              <Select
                value={newUser.organizationId}
                label="Organization ID"
                onChange={(e) => setNewUser(prev => ({ ...prev, organizationId: e.target.value }))}
                required
              >
                <MenuItem value="default-org">default-org</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateUser} 
            disabled={!newUser.email || !newUser.organizationId || creating}
          >
            {creating ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
